import { useDevice } from '@/stores/device.store';
import { useEmployeeStore } from '@/stores/employee';
import { useShiftStore } from '@/stores/shift';
import { usePrinterStore } from '@/stores/printer';
import { ElNotification } from 'element-plus';
import { getCurrencySymbol } from '@/utils/formatNumber';
import { PayInOutType } from '@/types/pay-in-out.type';
import { IPaymentMethod } from '@posx/core';
import { logInfo, logError } from '@/utils/logger';
import type { Ref } from 'vue';

interface MenuActionParams {
  t: (key: string) => string;
  currentShift: Ref<any>;
  visibleStaffShift: Ref<boolean>;
}

// Handles pay in/out business logic for Menu component
export async function handleMenuAction(action: string, params: MenuActionParams, ...args: any[]) {
  const { t, currentShift, visibleStaffShift } = params;
  const deviceStore = useDevice();
  const employeeStore = useEmployeeStore();
  const shiftStore = useShiftStore();
  const printerStore = usePrinterStore();
  try {
    switch (action) {
      case 'payIn': {
        if (!currentShift.value) {
          ElNotification({ customClass: 'x-notification__error', title: t('common.error'), message: t('common.noOpeningShift') });
          visibleStaffShift.value = true;
          return;
        }
        const method = args[2] as IPaymentMethod | undefined;
        await shiftStore.cashIn(parseFloat(args[0]), args[1] as string, method);
        if (method?.is_cash !== false) await deviceStore.openCashDrawer(); // only open drawer for cash
        await printerStore.createPayInPayoutPrintJob({ type: PayInOutType.IN, amount: parseFloat(args[0]), reason: args[1], payment_method_name: method?.name || 'Cash', employee_name: employeeStore.currentStaff?.name, timestamp: new Date() });
        ElNotification({ customClass: 'x-notification__success', title: t('common.success'), message: `${getCurrencySymbol()}${args[0]} ${t('till.successful_payin')}` });
        break;
      }
      case 'payOut': {
        if (!currentShift.value) {
          ElNotification({ customClass: 'x-notification__error', title: t('common.error'), message: t('common.noOpeningShift') });
          visibleStaffShift.value = true;
          return;
        }
        const method = args[2] as IPaymentMethod | undefined;
        await shiftStore.cashOut(parseFloat(args[0]), args[1] as string, method);
        if (method?.is_cash !== false) await deviceStore.openCashDrawer(); // only open drawer for cash
        await printerStore.createPayInPayoutPrintJob({ type: PayInOutType.OUT, amount: parseFloat(args[0]), reason: args[1], payment_method_name: method?.name || 'Cash', employee_name: employeeStore.currentStaff?.name, timestamp: new Date() });
        ElNotification({ customClass: 'x-notification__success', title: t('common.success'), message: `${getCurrencySymbol()}${args[0]} ${t('till.successful_payout')}` });
        break;
      }
      default:
        logInfo('Action not recognized', { action });
    }
  } catch (error: any) {
    ElNotification({ customClass: 'x-notification__error', title: t('common.error'), message: error.message || t('common.operationFailed') });
    logError('Handle action error', { action, error: error?.message });
  }
}
